import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, RotateCcw, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, ReadOnlyBanner } from "@/components/common";
import { FileDrop, MappingEditor, StatusPill, SummaryGrid } from "@/components/FileImport";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const FIELDS = [["code", "Employee Code", true], ["name", "Employee Name", true], ["department", "Department"], ["branch", "Branch / Location"]];
const TABS = [["all", "All"], ["new", "New"], ["update", "Name changed"], ["unchanged", "Already exists"], ["duplicate", "Duplicate"], ["invalid", "Invalid"]];

function PreviewTable({ rows, confirm, setConfirm }) {
  const toggle = (c, v) => { const s = new Set(confirm); v ? s.add(c) : s.delete(c); setConfirm(s); };
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto max-h-[480px]" data-testid="import-preview-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0">
          <tr><th className="px-3 py-2 text-left">Row</th><th className="px-3 py-2 text-left">Code</th><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left hidden md:table-cell">Department</th><th className="px-3 py-2 text-left hidden md:table-cell">Branch</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Details</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.row} className="border-t border-slate-100">
              <td className="px-3 py-2 text-slate-400">{r.row}</td>
              <td className="px-3 py-2 money text-blue-800">{r.code || "—"}</td>
              <td className="px-3 py-2">{r.name || "—"}</td>
              <td className="px-3 py-2 hidden md:table-cell">{r.department || "—"}</td>
              <td className="px-3 py-2 hidden md:table-cell">{r.branch || "—"}</td>
              <td className="px-3 py-2"><StatusPill status={r.status} /></td>
              <td className="px-3 py-2 text-xs">
                {r.error && <span className="text-red-700">{r.error}</span>}
                {r.status === "update" && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={confirm.has(r.code)} onCheckedChange={(v) => toggle(r.code, v)} data-testid={`confirm-update-${r.code}`} />
                    <span>Existing: <b>{r.existing_name}</b> → update to <b>{r.name}</b></span>
                  </label>
                )}
                {r.status === "unchanged" && <span className="text-slate-500">Existing record kept: {r.existing_name}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ImportEmployees() {
  const { can } = useAuth();
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("all");
  const [confirm, setConfirm] = useState(new Set());
  const [summary, setSummary] = useState(null);
  const [ask, setAsk] = useState(false);

  const parse = async (f, mapping) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("kind", "employees");
    if (mapping) fd.append("mapping", JSON.stringify(mapping));
    try {
      const { data } = await api.post("/import/parse", fd);
      setFile(f); setParsed(data); setConfirm(new Set()); setSummary(null); setTab("all");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/employees/commit", { rows: parsed.rows, confirm_codes: [...confirm], filename: parsed.filename });
      setSummary(data); setAsk(false);
      toast.success(`Import complete: ${data.imported} new, ${data.updated} updated`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setFile(null); setParsed(null); setSummary(null); };
  if (!can("admin")) return <div><PageHeader title="Excel Import" /><ReadOnlyBanner text="Only administrators can import employee master data." /></div>;
  const c = parsed?.counts || {};
  const rows = parsed?.rows.filter((r) => tab === "all" || r.status === tab) || [];

  return (
    <div>
      <PageHeader title="Employee Excel Import" subtitle="Import employee codes and names from Excel or CSV. Existing ledgers are never touched."
        actions={parsed && <Button variant="outline" onClick={reset} data-testid="import-reset-btn"><RotateCcw className="h-4 w-4" /> New file</Button>} />
      {summary ? (
        <div className="space-y-4 fade-up" data-testid="import-summary">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="h-5 w-5" /> Import completed for {parsed.filename}</div>
          <SummaryGrid testid="import-summary-grid" items={[
            ["total", "Total in file", summary.total], ["imported", "Imported", summary.imported, "text-blue-800"], ["updated", "Updated", summary.updated, "text-amber-700"],
            ["duplicates", "Duplicates", summary.duplicates, "text-orange-700"], ["invalid", "Invalid", summary.invalid, "text-red-700"],
          ]} />
          <div className="flex gap-2"><Button asChild className="bg-blue-800 hover:bg-blue-900"><Link to="/employees">View employees</Link></Button><Button variant="outline" asChild><Link to="/opening-balances">Enter opening balances</Link></Button></div>
        </div>
      ) : !parsed ? (
        <FileDrop onFile={(f) => parse(f)} busy={busy} testid="employee-import" template="employees" />
      ) : (
        <div className="space-y-4">
          <div className="text-sm text-slate-600">File: <b>{parsed.filename}</b> · {parsed.total} data rows</div>
          <MappingEditor key={JSON.stringify(parsed.mapping)} headers={parsed.headers} mapping={parsed.mapping} fields={FIELDS} busy={busy} testid="employee-import" onApply={(m) => parse(file, m)} />
          {parsed.missing_fields.length > 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="mapping-missing"><AlertTriangle className="h-4 w-4" /> Could not detect: {parsed.missing_fields.join(", ")}. Map the columns above and click Apply.</div>
          ) : (
            <>
              <div className="flex flex-wrap gap-1.5">
                {TABS.map(([k, l]) => (
                  <button key={k} onClick={() => setTab(k)} data-testid={`preview-tab-${k}`} className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${tab === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 hover:border-slate-400"}`}>
                    {l} ({k === "all" ? parsed.rows.length : c[k] || 0})
                  </button>
                ))}
              </div>
              <PreviewTable rows={rows} confirm={confirm} setConfirm={setConfirm} />
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl border p-4">
                <div className="text-sm text-slate-600">{c.new || 0} new will be added · {confirm.size} of {c.update || 0} name changes confirmed · {(c.invalid || 0) + (c.duplicate || 0)} rows will be skipped</div>
                <Button className="bg-blue-800 hover:bg-blue-900" disabled={busy || ((c.new || 0) + confirm.size === 0)} onClick={() => setAsk(true)} data-testid="import-commit-btn">Import employees</Button>
              </div>
            </>
          )}
        </div>
      )}
      <ConfirmDialog open={ask} onOpenChange={setAsk} busy={busy} onConfirm={commit} confirmText="Import now" testid="import-confirm-dialog"
        title="Confirm employee import" description={`Add ${c.new || 0} new employees and update ${confirm.size} employee names. No ledger or transaction will be changed.`} />
      {busy && parsed && <Loader2 className="fixed bottom-6 right-6 h-6 w-6 animate-spin text-blue-700" />}
    </div>
  );
}
